<template>
  <div class="vtextarea" :class="{ error: !!error, border: !border, loading }">
    <textarea
      ref="textarea"
      :class="[
        'textarea',
        {
          resizable: resizable,
          'resizable-y': resizableY,
          'resizable-x': resizableX,
          auto_height: auto_height,
          'no-overflow': withoutOverflow
        }
      ]"
      :name="name"
      :placeholder="placeholder"
      :rows="rows ? rows : 2"
      :value="value"
      :style="{ height: height ? height + 'px' : '' }"
      :autofocus="autofocus"
      :disabled="disabled || loading"
      :maxlength="maxLength"
      @input="handle_input"
      @blur="$emit('blur')"
      @keyup.alt.enter="$emit('keypress', $event.target.value)"
    ></textarea>

    <div v-if="!!error" class="message">{{ error }}</div>

    <slot></slot>
  </div>
</template>

<script>
export default {
  name: 'Vtextarea',

  props: {
    value: {
      type: [String, Number],
      default: ''
    },

    name: {
      type: [String, Number],
      default: ''
    },

    loading: {
      type: Boolean,
      default: false
    },

    placeholder: {
      type: [String, Number],
      default: ''
    },

    /* Для IE11 обязательное значение */
    height: {
      type: Number,
      default: 0
    },

    rows: {
      type: Number,
      default: 0
    },

    error: {
      type: String,
      default: ''
    },

    focus: {
      type: Boolean,
      default: false
    },

    autofocus: {
      type: Boolean,
      default: false
    },

    resizable: {
      type: Boolean,
      default: false
    },

    resizableY: {
      type: Boolean,
      default: false
    },

    resizableX: {
      type: Boolean,
      default: false
    },

    auto_height: {
      // обязательно исходные данные должны быть сразу загружены
      type: Boolean,
      default: false
    },

    min_height: {
      // обязательно исходные данные должны быть сразу загружены
      type: Number,
      default: 0
    },

    disabled: {
      type: Boolean,
      default: false
    },

    hasMaxHeight: {
      type: Boolean,
      default: false
    },

    border: {
      type: Boolean,
      default: true
    },

    preventPasteText: {
      type: Boolean,
      default: false
    },

    withoutOverflow: {
      type: Boolean,
      default: false
    },

    autoExpand: {
      type: Boolean,
      default: false
    },

    maxLength: {
      type: Number,
      default: 10000
    }
  },

  watch: {
    focus() {
      this.$refs.textarea.focus();
    }
  },

  mounted() {
    if (this.preventPasteText) {
      this.$refs.textarea.onpaste = e => {
        e.preventDefault();
        return false;
      };
    }
    // console.log('this.auto_height: ', this.auto_height);
    if (this.auto_height) {
      this.auto_expand();
    }

    this.$nextTick().then(() => {
      this.initialHeight = this.$refs.textarea.clientHeight;
    });
  },

  methods: {
    handle_input($event) {
      this.$emit('input', $event.target.value);

      if (this.auto_height) {
        this.auto_expand();
      }

      if (this.autoExpand) {
        this.auto_expand_new();
      }
    },

    auto_expand_new() {
      this.$nextTick().then(() => {
        this.$refs.textarea.style.height = '32px';

        if (this.hasMaxHeight) {
          this.$refs.textarea.style.maxHeight = this.height + 'px';
        }

        if (this.min_height && this.$refs.textarea.scrollHeight < this.min_height) {
          this.$refs.textarea.style.minHeight = this.min_height + 'px';
        }

        const newHeight = this.$refs.textarea.scrollHeight;

        if (newHeight > 225) {
          this.$refs.textarea.style.height = '225px';
          this.$refs.textarea.style.overflowY = 'auto';
        } else {
          this.$refs.textarea.style.height = newHeight + 'px';
          this.$refs.textarea.style.overflowY = 'hidden';
        }
      });
    },

    auto_expand() {
      // console.log('this.hasMaxHeight: ', this.hasMaxHeight);
      if (this.hasMaxHeight) {
        this.auto_expand_new();
      } else {
        // auto expand textarea height
        if (!this.min_height) {
          this.$refs.textarea.style.height = 'initial';
        }
        // console.log('this.min_height', this.min_height);

        const computed = window.getComputedStyle(this.$refs.textarea);
        // console.log('computed: ', computed);
        // console.log('this.$refs.textarea.scrollHeight: ', this.$refs.textarea.scrollHeight);
        // console.log('computed border-top-width: ', computed.getPropertyValue('border-top-width'));
        // console.log(
        //   'computed border-bottom-width: ',
        //   computed.getPropertyValue('border-bottom-width')
        // );
        let height =
          parseInt(computed.getPropertyValue('border-top-width'), 10) +
          this.$refs.textarea.scrollHeight +
          parseInt(computed.getPropertyValue('border-bottom-width'), 10);

        // console.log('height: ', height);
        // console.log('this.height: ', this.height);
        if (height < this.height) {
          height = this.height;
        }

        if (this.min_height && (height < this.min_height || height <= 58)) {
          height = this.min_height;
        }
        // console.log('height', height);
        this.$refs.textarea.style.height = height + 'px';

        if (this.hasMaxHeight) {
          this.$refs.textarea.style.maxHeight = this.height + 'px';
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.vtextarea {
  display: flex;
  margin-top: 0;

  .textarea {
    @include mixin-text-main;
    color: $black;
    min-width: 100%;
    max-width: 100%;
    min-height: 100%;
    max-height: 100%;
    display: block;
    padding: 10px 12px;
    margin: 0;
    border-radius: 2px;
    border: 1px solid $grey-light;
    resize: none;
    overflow-y: auto;
    line-height: 18px;

    &.resizable {
      resize: both;
      min-height: auto;
    }

    &.resizable-x {
      resize: horizontal !important;
      min-height: auto;
    }

    &.resizable-y {
      resize: vertical !important;
      min-height: 40px;
    }

    &.auto_height {
      resize: both;
      max-height: auto;
    }

    &.no-overflow {
      overflow: hidden;
    }
  }

  &.border {
    .textarea {
      border: none;
      padding: 0;
    }
  }

  &.error {
    flex-direction: column;

    .textarea {
      border-color: $red;
    }

    .message {
      @include mixin-text-tag;
      color: $red;
      margin-top: 5px;
      margin-left: 10px;
    }
  }
}
</style>
